import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { FileSpreadsheet, Image, FolderArchive, Building2, CalendarDays, Lock, GitCompare, MapPin } from "lucide-react";
import { PageLayout } from "@/components/templates/page-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSites } from "@/core/hooks/useSites";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, type OfflineCampagne } from "@/core/offline/db";
import { downloadBlob } from "@/core/utils/file-download";
import { fetchSiteExportData } from "@/modules/corrosion/export/data-fetcher";
import { generateExcelBlob, generateComparativeExcelBlob } from "@/modules/corrosion/export/excel-export";
import { generateAnnotatedPlanBlob } from "@/modules/corrosion/export/plan-export";
import { generateZipBlob } from "@/modules/corrosion/export/zip-export";

type ExportType = "excel" | "plans" | "zip" | "excel-comparatif";

export default function ExportsPage() {
  const { sites } = useSites();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pré-sélection via URL : ?siteId=X&campagneId=Y
  const urlSiteId = searchParams.get("siteId");
  const urlCampagneId = searchParams.get("campagneId");

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(urlSiteId);
  const [selectedCampagneId, setSelectedCampagneId] = useState<string | null>(urlCampagneId);
  const [selectedRefCampagneId, setSelectedRefCampagneId] = useState<string | null>(null);
  const [loading, setLoading] = useState<ExportType | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sync l'état si l'URL change
  useEffect(() => {
    if (urlSiteId && urlSiteId !== selectedSiteId) setSelectedSiteId(urlSiteId);
    if (urlCampagneId && urlCampagneId !== selectedCampagneId) setSelectedCampagneId(urlCampagneId);
  }, [urlSiteId, urlCampagneId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compter les mesures par site
  const mesureCounts = useLiveQuery(async () => {
    const counts: Record<string, number> = {};
    for (const site of sites) {
      const pins = await offlineDb.pins.where("siteId").equals(site.id).toArray();
      const pinIds = pins.map((p) => p.id);
      counts[site.id] = pinIds.length === 0
        ? 0
        : await offlineDb.mesures.where("pinId").anyOf(pinIds).count();
    }
    return counts;
  }, [sites]) ?? {};

  // Compter les zones par site
  const zoneCounts = useLiveQuery(async () => {
    const counts: Record<string, number> = {};
    for (const site of sites) {
      counts[site.id] = await offlineDb.zones.where("siteId").equals(site.id).count();
    }
    return counts;
  }, [sites]) ?? {};

  const selectedSite = sites.find((s) => s.id === selectedSiteId);

  // Campagnes du site sélectionné (triées par date desc)
  const siteCampagnes = useLiveQuery(async () => {
    if (!selectedSiteId) return [];
    const list = await offlineDb.campagnes
      .where("siteId")
      .equals(selectedSiteId)
      .toArray();
    return list.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [selectedSiteId]) ?? [];

  // Campagne effectivement sélectionnée (avec défaut : active ou la plus récente)
  const defaultCampagneId = useMemo(() => {
    if (siteCampagnes.length === 0) return null;
    const active = siteCampagnes.find((c) => c.status === "active");
    return active?.id ?? siteCampagnes[0].id;
  }, [siteCampagnes]);

  const effectiveCampagneId = selectedCampagneId ?? defaultCampagneId;
  const selectedCampagne = siteCampagnes.find((c) => c.id === effectiveCampagneId);

  function changeSite(id: string | null) {
    setSelectedSiteId(id);
    setSelectedCampagneId(null);
    setSelectedRefCampagneId(null);
    // Nettoyer l'URL
    const next = new URLSearchParams();
    if (id) next.set("siteId", id);
    setSearchParams(next, { replace: true });
  }

  function changeCampagne(id: string) {
    setSelectedCampagneId(id);
    const next = new URLSearchParams();
    if (selectedSiteId) next.set("siteId", selectedSiteId);
    next.set("campagneId", id);
    setSearchParams(next, { replace: true });
  }

  async function handleExport(type: ExportType) {
    if (!selectedSiteId) return;
    setLoading(type);
    setError(null);

    try {
      const data = await fetchSiteExportData(selectedSiteId, effectiveCampagneId ?? undefined);
      if (!data) {
        setError("Site ou campagne introuvable.");
        setLoading(null);
        return;
      }

      const siteName = data.site.name.replace(/[/\\:*?"<>|]/g, "_");
      const campagneDate = new Date(data.campagne.date)
        .toISOString()
        .slice(0, 10); // YYYY-MM-DD
      const baseName = `${siteName}_${campagneDate}`;

      if (type === "excel") {
        const blob = await generateExcelBlob(data);
        downloadBlob(blob, `${baseName}.xlsx`);
      } else if (type === "excel-comparatif") {
        if (!selectedRefCampagneId) {
          setError("Sélectionnez une campagne de référence pour la comparaison.");
          setLoading(null);
          return;
        }
        const dataRef = await fetchSiteExportData(selectedSiteId, selectedRefCampagneId);
        if (!dataRef) {
          setError("Campagne de référence introuvable.");
          setLoading(null);
          return;
        }
        const refDate = new Date(dataRef.campagne.date).toISOString().slice(0, 10);
        const blob = await generateComparativeExcelBlob(data, dataRef);
        downloadBlob(blob, `${baseName}_vs_${refDate}_comparatif.xlsx`);
      } else if (type === "plans") {
        let exported = 0;
        for (const zoneData of data.zones) {
          const blob = await generateAnnotatedPlanBlob(zoneData);
          if (blob) {
            const zoneName = zoneData.zone.name.replace(/[/\\:*?"<>|]/g, "_");
            downloadBlob(blob, `${baseName}_${zoneName}_plan.png`);
            exported++;
          }
        }
        if (exported === 0) {
          setError("Aucune zone n'a de plan importé.");
        }
      } else if (type === "zip") {
        const blob = await generateZipBlob(data);
        downloadBlob(blob, `${baseName}_export.zip`);
      }
    } catch (err) {
      console.error("Export error:", err);
      setError(`Erreur lors de l'export : ${err instanceof Error ? err.message : "inconnue"}`);
    } finally {
      setLoading(null);
    }
  }

  const formatCampagneLabel = (c: OfflineCampagne) => {
    const date = new Date(c.date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const status = c.status === "active" ? " (active)" : " (clôturée)";
    return `Campagne du ${date}${status}`;
  };

  return (
    <PageLayout title="Exports">
      {sites.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          Aucun site. Créez un site d&apos;abord pour pouvoir exporter.
        </div>
      ) : selectedSite ? (
        /* ─── Site sélectionné : sélecteur campagne + boutons d'export ─── */
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="size-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold">{selectedSite.name}</h3>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                {selectedSite.client && <span>{selectedSite.client}</span>}
                <span>{zoneCounts[selectedSite.id] ?? 0} zone{(zoneCounts[selectedSite.id] ?? 0) > 1 ? "s" : ""}</span>
                <span>{mesureCounts[selectedSite.id] ?? 0} mesure{(mesureCounts[selectedSite.id] ?? 0) > 1 ? "s" : ""}</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => changeSite(null)}
            >
              Changer
            </Button>
          </div>

          {/* Sélecteur de campagne */}
          {siteCampagnes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              Aucune campagne sur ce site. Crée-en une d'abord depuis la page du site.
            </div>
          ) : (
            <div className="space-y-2">
              <label htmlFor="campagne-select" className="text-sm font-medium flex items-center gap-2">
                <CalendarDays className="size-4 text-muted-foreground" />
                Campagne à exporter
              </label>
              <select
                id="campagne-select"
                value={effectiveCampagneId ?? ""}
                onChange={(e) => changeCampagne(e.target.value)}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                {siteCampagnes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {formatCampagneLabel(c)}
                  </option>
                ))}
              </select>
              {selectedCampagne && (
                <p className="text-xs text-muted-foreground">
                  {selectedCampagne.status === "cloturee" ? (
                    <span className="inline-flex items-center gap-1">
                      <Lock className="size-3" />
                      Snapshot figé au{" "}
                      {new Date(selectedCampagne.updatedAt).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  ) : (
                    "Campagne en cours : les valeurs exportées reflètent l'état actuel."
                  )}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button
              size="lg"
              className="justify-start gap-3 h-14"
              onClick={() => handleExport("excel")}
              loading={loading === "excel"}
              disabled={loading !== null || !effectiveCampagneId}
            >
              <FileSpreadsheet className="size-5" />
              <div className="text-left">
                <div className="font-medium">Excel (.xlsx)</div>
                <div className="text-xs opacity-70">Tableau des mesures par zone</div>
              </div>
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="justify-start gap-3 h-14"
              onClick={() => handleExport("plans")}
              loading={loading === "plans"}
              disabled={loading !== null || !effectiveCampagneId}
            >
              <Image className="size-5" />
              <div className="text-left">
                <div className="font-medium">Plans annotés (.png)</div>
                <div className="text-xs opacity-70">Plans avec pins colorés et légende</div>
              </div>
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="justify-start gap-3 h-14"
              onClick={() => handleExport("zip")}
              loading={loading === "zip"}
              disabled={loading !== null || !effectiveCampagneId}
            >
              <FolderArchive className="size-5" />
              <div className="text-left">
                <div className="font-medium">ZIP complet</div>
                <div className="text-xs opacity-70">Excel + plans + photos</div>
              </div>
            </Button>

            {/* Comparatif N vs N-X : visible uniquement si >= 2 campagnes */}
            {siteCampagnes.length >= 2 && (
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <GitCompare className="size-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Comparatif N vs N-X</span>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">
                    Campagne de référence (N-X)
                  </label>
                  <select
                    value={selectedRefCampagneId ?? ""}
                    onChange={(e) => setSelectedRefCampagneId(e.target.value || null)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                  >
                    <option value="">Choisir une campagne...</option>
                    {siteCampagnes
                      .filter((c) => c.id !== effectiveCampagneId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {formatCampagneLabel(c)}
                        </option>
                      ))}
                  </select>
                </div>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full justify-start gap-3 h-14"
                  onClick={() => handleExport("excel-comparatif")}
                  loading={loading === "excel-comparatif"}
                  disabled={loading !== null || !effectiveCampagneId || !selectedRefCampagneId}
                >
                  <FileSpreadsheet className="size-5" />
                  <div className="text-left">
                    <div className="font-medium">Excel comparatif (.xlsx)</div>
                    <div className="text-xs opacity-70">
                      {selectedRefCampagneId
                        ? `${formatCampagneLabel(siteCampagnes.find(c => c.id === effectiveCampagneId)!)} vs ${formatCampagneLabel(siteCampagnes.find(c => c.id === selectedRefCampagneId)!)}`
                        : "Sélectionnez une campagne de référence"}
                    </div>
                  </div>
                </Button>
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive mt-2">{error}</p>
          )}
        </div>
      ) : (
        /* ─── Pas de site sélectionné : liste des sites ─── */
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            Choisir un site à exporter
          </h3>
          <div className="flex flex-col gap-2">
            {sites.map((site) => (
              <Card
                key={site.id}
                className="cursor-pointer shadow-sm transition-all active:scale-[0.98] hover:shadow-md"
                onClick={() => changeSite(site.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="size-4 text-muted-foreground" />
                    <CardTitle className="text-base">{site.name}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {site.client && <span>{site.client}</span>}
                    <span className="flex items-center gap-1">
                      <MapPin className="size-3" />
                      {zoneCounts[site.id] ?? 0} zone{(zoneCounts[site.id] ?? 0) > 1 ? "s" : ""}
                    </span>
                    <span>
                      {mesureCounts[site.id] ?? 0} mesure{(mesureCounts[site.id] ?? 0) > 1 ? "s" : ""}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </PageLayout>
  );
}

